import type { Map } from 'maplibre-gl';
import type { StyleSpecification } from 'maplibre-gl';
import type { HeadingData } from '../sensors/heading';

// Fonction pour calculer si c'est l'heure du mode nuit (coucher du soleil + 15min)
function isNightTime(): boolean {
  const now = new Date();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  
  // Approximation simple : mode nuit de 20h15 à 6h00
  // (Coucher du soleil moyen ~20h + 15min)
  const currentTimeMinutes = hours * 60 + minutes;
  const nightStartMinutes = 20 * 60 + 15; // 20h15
  const nightEndMinutes = 6 * 60; // 6h00
  
  return currentTimeMinutes >= nightStartMinutes || currentTimeMinutes < nightEndMinutes;
}

function getMapStyle(): StyleSpecification {
  // Vérifier d'abord les préférences utilisateur
  const savedTheme = localStorage.getItem('themeMode') as 'day' | 'night' | 'auto';
  
  let shouldUseNight = false;
  if (savedTheme === 'night') {
    shouldUseNight = true;
  } else if (savedTheme === 'day') {
    shouldUseNight = false;
  } else {
    // Mode auto ou pas de préférence : selon l'heure
    shouldUseNight = isNightTime();
  }
  
  if (shouldUseNight) {
    // Style nuit avec tuiles CartoDB Dark Matter
    return {
      version: 8,
      glyphs: 'https://tiles.stadiamaps.com/fonts/{fontstack}/{range}.pbf',
      sources: {
        osm: {
          type: 'raster',
          tiles: [
            'https://cartodb-basemaps-a.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png',
            'https://cartodb-basemaps-b.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png',
            'https://cartodb-basemaps-c.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png'
          ],
          tileSize: 256,
          minzoom: 0,
          maxzoom: 19,
          attribution: '© OpenStreetMap contributors, © CARTO'
        }
      },
      layers: [
        {
          id: 'osm',
          type: 'raster',
          source: 'osm',
          minzoom: 0,
          maxzoom: 22
        }
      ]
    };
  }
  
  // Style jour avec OpenStreetMap standard
  return {
    version: 8,
    glyphs: 'https://tiles.stadiamaps.com/fonts/{fontstack}/{range}.pbf',
    sources: {
      osm: {
        type: 'raster',
        tiles: [
          'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
        ],
        tileSize: 256,
        minzoom: 0,
        maxzoom: 19,
        attribution: '© OpenStreetMap contributors'
      }
    },
    layers: [
      {
        id: 'osm',
        type: 'raster',
        source: 'osm',
        minzoom: 0,
        maxzoom: 22
      }
    ]
  };
}

// Fonctions pour forcer un style spécifique
export function getDayStyle(): StyleSpecification {
  return {
    version: 8,
    glyphs: 'https://tiles.stadiamaps.com/fonts/{fontstack}/{range}.pbf',
    sources: {
      osm: {
        type: 'raster',
        tiles: [
          'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
          'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
        ],
        tileSize: 256,
        minzoom: 0,
        maxzoom: 19,
        attribution: '© OpenStreetMap contributors'
      }
    },
    layers: [
      {
        id: 'osm',
        type: 'raster',
        source: 'osm',
        minzoom: 0,
        maxzoom: 22
      }
    ]
  };
}

export function getNightStyle(): StyleSpecification {
  return {
    version: 8,
    glyphs: 'https://tiles.stadiamaps.com/fonts/{fontstack}/{range}.pbf',
    sources: {
      osm: {
        type: 'raster',
        tiles: [
          'https://cartodb-basemaps-a.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png',
          'https://cartodb-basemaps-b.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png',
          'https://cartodb-basemaps-c.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png'
        ],
        tileSize: 256,
        minzoom: 0,
        maxzoom: 19,
        attribution: '© OpenStreetMap contributors, © CARTO'
      }
    },
    layers: [
      {
        id: 'osm',
        type: 'raster',
        source: 'osm',
        minzoom: 0,
        maxzoom: 22
      }
    ]
  };
}

// Export de la fonction pour permettre la régénération
export { getMapStyle };
export const mapStyle = getMapStyle();

class BearingController {
  private map: Map | null = null;
  private targetBearing: number = 0;
  private currentBearing: number = 0;
  private smoothedBearing: number = 0;
  private lastUpdateTime: number = 0;
  private readonly smoothingFactor: number = 0.3; // Facteur de lissage plus agressif pour les virages
  private readonly minDeltaThreshold: number = 0.5; // Seuil minimal pour bouger encore réduit

  setMap(map: Map) {
    this.map = map;
    this.currentBearing = map.getBearing();
    this.smoothedBearing = this.currentBearing;
  }

  updateBearing(heading: HeadingData) {
    if (!this.map || heading.source === null) return;
    
    // Ne pas mettre à jour si on est stationnaire
    if (heading.source === 'waiting') {
      return;
    }
    
    this.targetBearing = heading.bearingDeg;
    
    // Premier bearing : initialisation directe
    if (this.lastUpdateTime === 0) {
      this.smoothedBearing = this.targetBearing;
      this.currentBearing = this.targetBearing;
      this.map.setBearing(this.smoothedBearing);
      this.lastUpdateTime = Date.now();
      return;
    }
    
    // Lissage normal
    const delta = this.shortestAngularDistance(this.smoothedBearing, this.targetBearing);
    
    if (Math.abs(delta) > this.minDeltaThreshold) {
      this.smoothedBearing = this.normalizeBearing(
        this.smoothedBearing + delta * this.smoothingFactor
      );
    }
    
    // Appliquer avec throttling
    const now = Date.now();
    if (now - this.lastUpdateTime > 50) {
      this.map.setBearing(this.smoothedBearing);
      this.currentBearing = this.smoothedBearing;
      this.lastUpdateTime = now;
    }
  }


  private shortestAngularDistance(from: number, to: number): number {
    const diff = to - from;
    if (diff > 180) return diff - 360;
    if (diff < -180) return diff + 360;
    return diff;
  }

  private normalizeBearing(bearing: number): number {
    let normalized = bearing % 360;
    if (normalized < 0) normalized += 360;
    return normalized;
  }

  resetBearing() {
    if (!this.map) return;
    
    console.log('🔄 Réinitialisation du bearing à 0°');
    this.targetBearing = 0;
    this.smoothedBearing = 0;
    this.currentBearing = 0;
    this.lastUpdateTime = 0;
    
    // Animation douce pour revenir au nord
    this.map.easeTo({
      bearing: 0,
      duration: 800,
      easing: (t) => t // Linéaire pour éviter le rebond
    });
  }
  
  destroy() {
    // Plus besoin d'animation frame
  }
}

export const bearingController = new BearingController();